import { createSignal, onCleanup, onMount, Show } from "solid-js";
import type { BriefQuestion } from "./briefTypes";

export function BriefQuestionResponseSheet(props: {
  question: BriefQuestion;
  returnFocus: HTMLElement | null;
  onSave: (response: string, confirmationSource: string) => void;
  onClose: () => void;
}) {
  let panel: HTMLElement | undefined;
  let responseField: HTMLTextAreaElement | undefined;
  const [response, setResponse] = createSignal(props.question.response);
  const [confirmationSource, setConfirmationSource] = createSignal(
    props.question.confirmationSource,
  );
  const [error, setError] = createSignal<string | null>(null);

  onMount(() => queueMicrotask(() => responseField?.focus()));
  onCleanup(() => queueMicrotask(() => props.returnFocus?.focus()));

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      props.onClose();
      return;
    }
    if (event.key !== "Tab" || !panel) return;
    const controls = focusableControls(panel);
    const first = controls[0];
    const last = controls.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const save = (event: SubmitEvent) => {
    event.preventDefault();
    const nextResponse = response().trim();
    if (!nextResponse) {
      setError("Enter a response before recording this input.");
      responseField?.focus();
      return;
    }
    props.onSave(nextResponse, confirmationSource().trim());
  };

  return (
    <div class="brief-sheet-layer">
      <button
        type="button"
        class="brief-sheet-scrim"
        aria-label="Close response editor"
        onClick={props.onClose}
      />
      <aside
        ref={panel}
        class="brief-sheet brief-response-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="brief-response-title"
        onKeyDown={onKeyDown}
      >
        <header>
          <div>
            <span class="signal-mini-kicker">Preview edit</span>
            <h2 id="brief-response-title">Record response</h2>
          </div>
          <button
            type="button"
            class="brief-sheet-close"
            aria-label="Close response editor"
            title="Close response editor"
            onClick={props.onClose}
          >
            x
          </button>
        </header>
        <form class="brief-edit-form brief-response-form" onSubmit={save}>
          <p class="brief-response-question">{props.question.text}</p>
          <Show when={error()}>
            {(message) => <p role="alert">{message()}</p>}
          </Show>
          <label>
            <span>Response or decision</span>
            <textarea
              ref={responseField}
              rows={5}
              value={response()}
              onInput={(event) => {
                setResponse(event.currentTarget.value);
                if (error()) setError(null);
              }}
            />
          </label>
          <label>
            <span>Response source (optional)</span>
            <input
              value={confirmationSource()}
              onInput={(event) =>
                setConfirmationSource(event.currentTarget.value)
              }
            />
          </label>
          <div class="brief-edit-actions">
            <button type="button" onClick={props.onClose}>
              Cancel
            </button>
            <button type="submit" class="brief-primary-action">
              Apply preview change
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

function focusableControls(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
}
